import introJs from 'intro.js'
import { debounce } from 'lodash'
import { useEffect, useRef } from 'react'
import { useTranslate } from 'react-admin'
import usePrevious from './usePrevious'
import useIsMobileScreen from './useIsMobileScreen'
import isUserAddonsValid from '../utils/helpers/isUserAddonsValid'
import isStatistician from '../utils/helpers/isStatistician'
import createSteps from '../utils/constants/createSteps'
import { setLocalStorageValue } from '../utils/helpers/localStorageHelperMethods'
import { USER_ADDONS } from '../utils/constants/userAddons'

// enabled: indicates whether the tour is starting or not
// tourName: the string that provides the tour name
// steps: a function that returns an array of steps for the tour
// addon: the addons of the user that are allowed to see the tour

export default function useTour({ elRef, tourName, tourSteps, addon = USER_ADDONS.ALL, tourDesc = null }) {
  const previousEl = usePrevious(elRef)
  const translate = useTranslate()
  // Closes the running tour, if any, without marking it as seen
  const stopTourRef = useRef(() => {})
  // The tour points at desktop-only controls; it isn't marked as seen, so it still runs on a desktop.
  const isMobileScreen = useIsMobileScreen()

  const shouldTourStart = () => {
    const tourNameValue = window.localStorage.getItem(tourName)
    if (!tourNameValue) return true
    if (!JSON.parse(tourNameValue)) return true
    if (tourNameValue !== tourDesc && !!tourDesc) return true
    return false
  }
  const stepIsNotHidden = (step) => {
    if (!Object.prototype.hasOwnProperty.call(step, 'element')) {
      return true
    }
    return step.element && !!step.element.getClientRects().length
  }

  // We use the enable variable to stop intro from running again every time the component is rerendering.
  useEffect(() => {
    if (
      previousEl !== elRef &&
      !isMobileScreen &&
      !isStatistician() &&
      isUserAddonsValid(addon) &&
      shouldTourStart()
    ) {
      const steps = createSteps(tourSteps, translate)
      if (!steps) {
        return
      }
      stopTourRef.current()
      // A fresh instance per tour: CalendarPage starts the hint tour right after the intro tour exits
      const intro = introJs()
      let active = true
      let leaving = false
      intro.setOptions({
        exitOnEsc: false,
        exitOnOverlayClick: false,
        overlayOpacity: 0.8,
        skipLabel: 'Skip',
        nextLabel: 'Next',
        prevLabel: 'Previous',
        doneLabel: 'Finish!',
        showBullets: false,
        showProgress: steps?.length > 1,
        showStepNumbers: false,
        disableInteraction: false,
        steps: steps.filter((step) => stepIsNotHidden(step)),
        showButtons: steps?.length > 1,
      })
      intro.start()
      const upDateTourSteps = debounce(() => {
        // We update the tour steps every time the window resizes to avoid showing intro on hidden objects
        if (steps) {
          intro.setOptions({ steps: steps.filter((step) => stepIsNotHidden(step)) })
          intro.refresh(true)
        }
      }, 200)

      // Close Intro.js when the back button is pressed
      const onBackButton = () => {
        window.history.replaceState({ introBackButton: true }, document.title)
        intro.exit()
      }

      const finish = () => {
        active = false
        window.removeEventListener('resize', upDateTourSteps)
        window.removeEventListener('popstate', onBackButton)
      }

      window.addEventListener('resize', upDateTourSteps)
      window.addEventListener('popstate', onBackButton)
      stopTourRef.current = () => {
        if (!active) return
        leaving = true
        finish()
        intro.exit(true)
      }
      intro.onexit(() => {
        finish()
        // We create the key when user exits the intro
        if (!leaving && window.history.state && !window.history.state.introBackButton) {
          setLocalStorageValue([
            {
              id: tourName,
              value: tourDesc !== null ? tourDesc : true,
            },
          ])
        }
      })
      intro.oncomplete(() => {
        finish()
        // We create the key when user completes the intro
        setLocalStorageValue([
          {
            id: tourName,
            value: tourDesc !== null ? tourDesc : true,
          },
        ])
      })
    }
  }, [elRef, previousEl, addon, tourName, tourSteps, translate])

  // Leaving the page mid-tour must not leave the overlay behind
  useEffect(() => () => stopTourRef.current(), [])
}
